import { create } from 'zustand';
import moment from 'moment';
import { config } from '../config';
import {
  CLIENT_TYPES,
  SESSION_EVENTS,
  SyncClient,
} from '@smarterservices/sync-client';
import { getMediaAuthToken } from '../helpers/utilities';
import CONSTANTS from '../config/constants';
import SessionTrack from '../classes/SessionTrack';
export const createExamSessionStore = (initialState = {}) =>
  create((set, get) => ({
    stores: {},
    sessionSid: null,
    examSid: null,
    installSid: null,
    courseSid: null,
    enrollmentSid: null,
    userSid: null,
    status: null,
    sessionDuration: 0, // this will represent the duration of the session, if live, it will be the duration plus 30 minutes. If recorded it will be the actual session duration.
    duration: 0,
    activeSessionStartTime: 0, // default this to now - 5 minutes...but this would be the actual field...
    recordingStartDate: null, // The ISO8601 date of when the recording started.
    recordingEndDate: null, // The ISO8601 date of when the recording ended.
    recordingStatus: null, // The status of the recording. Valid options are in CONSTANTS file...
    canHaveLiveVideo: false,
    isMediaServerRecording: false, // Flag that indicates if recording is active on the media server...
    sessionData: {},
    parentSessionData: null,
    childSessionData: null,
    incidentReport: null,
    courseData: {},
    userData: {},
    sync: {
      client: null,
      doc: null,
      emitter: null,
    },
    journey: {},
    annotationTypes: {},
    currentModalityOptions: [], // The modality options that are currently enabled and enforced.
    modalityOptions: {}, // The modality options that are enabled for this exam in the configuration.
    modalityOptionGroups: {}, // This is all possible modality options and their groups
    managingAnnotation: null, // The annotation that is being managed.
    annotations: {
      flags: [],
      events: [],
      notes: [],
      network: [],
      phases: [],
    },

    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    initialize: async (options) => {
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

      const initialSettings = {
        sessionSid: options.sessionSid,
      };

      set(initialSettings);

      // Load the session information....
      const sessionData = await get()
        .storeState('appStore')
        .ApiClient.getSession({
          sessionSid: options.sessionSid,
          examSid: options.examSid,
          installSid: options.installSid,
        });

      if (sessionData.successful) {
        const dataToUpdate = {
          sessionData: sessionData.data,
          examSid: sessionData.data.examSid,
          installSid: sessionData.data.installSid,
          enrollmentSid: sessionData.data.enrollmentSid,
          userSid: sessionData.data.userSid,
          courseSid: sessionData.data.courseSid,
        };

        if (
          sessionData.data.status.toLowerCase() === 'closed' ||
          sessionData.data.status.toLowerCase() === 'cancelled'
        ) {
          //dataToUpdate.sessionDuration = sessionData.data?.duration; // TODO: Remove this, it was just a fallback...

          // set the duration on the playback store as well.
          get()
            .storeState('playerStore')
            .setDuration(dataToUpdate.sessionDuration);
          get().storeState('playerStore').setCanHaveLiveVideo(false);
        } else if (CONSTANTS.STATUSES.CAN_HAVE_LIVE_VIDEO.includes(sessionData.data.status.toLowerCase())) {
          get().storeState('playerStore').setCanHaveLiveVideo(true);
        } else if (
          sessionData.data.status.toLowerCase() !== 'closed' &&
          sessionData.data.status.toLowerCase() !== 'cancelled'
        ) {
          // connect to sync if the session is not closed or cancelled....
          await get().connectToSync();
        }

        set(dataToUpdate);

        // Go ahead and call to load the related data now too,
        await get().loadRelatedSession("parent");
        await get().loadRelatedSession("child");

        // update the recording information..
        await get().updateRecordingMetadata();

        get().setStatus(sessionData.data.status);

        // set the modality options up...
        const modalityOptions =
          sessionData.data.examConfiguration.modalityOptions[
          sessionData.data.proctorTypeSid
          ];

        set({ modalityOptions });

        // Now load the annotation information...
        await get().loadAnnotations([
          'flags',
          'events',
          'notes',
          'network',
          'phases',
        ]);

        // Load the journey data.
        await get().refreshSessionJourney();

        // Now we need to load the course information...
        const courseData = await get()
          .storeState('appStore')
          .ApiClient.getCourse({
            installSid: get().installSid,
            courseSid: get().courseSid,
          });

        if (courseData.successful) {
          set({ courseData: courseData.data });
        } else {
          //TODO: IF this could not be loaded....we need to show an error message...
        }

        // Load the user info..
        const userData = await get()
          .storeState('appStore')
          .ApiClient.getUserProfile({
            installSid: get().installSid,
            userSid: get().userSid,
          });

        if (userData.successful) {
          set({ userData: userData.data });

        } else {
          //TODO: IF this could not be loaded....we need to show an error message...
        }

        // Load the incident reports
        await get().getIncidentReport();


      } else {
        //TODO: IF this could not be loaded....we need to show an error message...
      }
    },

    loadRelatedSession: async (type) => {
      const sessionData = {
        ...get().sessionData,
      };
      let sessionSid;

      if (type === "parent"
        && sessionData?.series
        && sessionData?.series?.relatedSessions
        && sessionData?.series?.relatedSessions?.parentSid) {
        sessionSid = sessionData?.series?.relatedSessions?.parentSid;
      } else if (type === "child"
        && sessionData?.series
        && sessionData?.series?.relatedSessions
        && sessionData?.series?.relatedSessions?.childSid) {
        sessionSid = sessionData?.series?.relatedSessions?.childSid;
      }

      // If there is a related session, snag the details...
      if (sessionSid) {
        const relatedSessionData = await get()
          .storeState('appStore')
          .ApiClient.getSession({
            sessionSid: sessionSid,
            examSid: get().examSid,
            installSid: get().installSid,
          });

        if (relatedSessionData.successful) {
          if (type === "parent") {
            set({ parentSessionData: relatedSessionData.data });
          } else {
            set({ childSessionData: relatedSessionData.data });
          }
        }
      }
    },

    setRecordingMetadata: (data) => {
      const localSessionData = {
        ...get().sessionData,
      };

      //console.log('setRecordingMetadata', data);

      if (data.startDate) {
        localSessionData.recordingStartDate = data.startDate;
      }
      if (data.status) {
        localSessionData.recordingStatus = data.status;
        set({ recordingStatus: data.status });
      }

      if (data.duration) {
        localSessionData.duration = data.duration;
        get().storeState('playerStore').setDuration(data.duration);
      } else if (data.endDate && localSessionData.recordingStartDate) {
        localSessionData.recordingEndDate = data.endDate;
        const videoDuration = Math.ceil(
          moment
            .utc(localSessionData.recordingEndDate)
            .diff(localSessionData.recordingStartDate, 'seconds', true)
        );
        localSessionData.duration = videoDuration;
        get().storeState('playerStore').setDuration(videoDuration);
      }
      //console.log('setRecordingMetadata', localSessionData);
      set({ sessionData: localSessionData });


      // update the player tracks now...
      if (data?.tracks) {
        Object.keys(data.tracks).forEach((trackId) => {
          const track = data.tracks[trackId];
          const sessionTrack = new SessionTrack();
          sessionTrack.populateFromHls(track);

          get().storeState('playerStore').addTrack(sessionTrack);
        });
      }
      // after updating the recording end date, we need to update the player...
      get().storeState('playerStore').updatePlayer();
    },

    /**
     * Updates the recording status for the current session. This will also update the store state on the playerStore to reflect
     * the recording status. This does not control if the session is actually being recorded, this is only the status of the actual re
     * recorded video.
     * @param {string} status - The current recording status. Should be one of ["live", "complete", "cancelled", "failed"]
     * @returns {undefined}
     */
    setRecordingStatus: (status) => {
      const localSessionData = {
        ...get().sessionData,
      };
      localSessionData.recordingStatus = status;
      set({ recordingStatus: localSessionData });
    },

    updateRecordingMetadata: async () => {
      // get the recording metadata.
      const recordingData = await get()
        .storeState('appStore')
        .ApiClient.getRecordingMetadata({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
        });

      if (recordingData.successful) {
        get().setRecordingMetadata({
          startDate: recordingData.data.startDate,
          endDate: recordingData.data.endDate,
          status: recordingData.data.status,
          duration: recordingData.data.duration,
          tracks: recordingData.data.tracks,
        });
      } else {
        // TODO: Handle error
      }
    },

    setIsMediaServerRecording: async (isMediaServerRecording) => {
      // since the media server is recording, lets setup the base recording dates...
      await get().updateRecordingMetadata();

      set({ isMediaServerRecording });
    },

    connectToSync: async () => {
      const syncClientConfig = {
        apiUrl: config.proctoring.api.baseUrl,
        documentId: `Session_${get().sessionSid}`,
        identity: `viewer_${get().storeState('appStore').identity}_${get().sessionSid}`,
        token: get().storeState('appStore').spToken,
        type: CLIENT_TYPES.SESSION,
      };

      //console.log(`Sync Client Config`, syncClientConfig);

      const localSync = { ...get().sync };

      localSync.client = await SyncClient(syncClientConfig);

      localSync.doc = await localSync.client.getSync();

      // attach the event emitter to the store...
      localSync.emitter = localSync.client.getEventEmitter();

      // set in state...
      set({
        sync: localSync,
        currentModalityOptions: localSync.doc?.currentModalityOptions || [],
      });

      // setup the sync listeners...
      get().setupSyncListeners();
    },

    refreshSessionJourney: async () => {
      const journeyData = await get()
        .storeState('appStore')
        .ApiClient.getSessionJourney({
          sessionSid: get().sessionSid,
          examSid: get().examSid,
          installSid: get().installSid,
        });

      if (journeyData.successful) {
        set({ journey: journeyData.data });
      } else {
        //TODO: Handle the error
      }
    },

    setSessionAsReviewed: async ({ reviewByUser }) => {
      const reviewCompletedDate = new Date().toISOString();

      const result = await get()
        .storeState('appStore')
        .ApiClient.updateSessionReviewStatus({
          sessionSid: get().sessionSid,
          examSid: get().examSid,
          installSid: get().installSid,
          payload: {
            reviewCompletedDate,
            reviewByUser,
            reviewed: true,
          }
        });

      if (result.successful) {
        // copy session data
        const localSessionData = {
          ...get().sessionData,
        };
        localSessionData.reviewCompletedDate = reviewCompletedDate;
        localSessionData.reviewByUser = reviewByUser;
        localSessionData.reviewed = true;
        set({ sessionData: localSessionData });
      } else {
        //TODO: Handle the error
      }
    },

    getCurrentJourneyStep: () => {
      const currentStepId = get().journey?.currentStepId;
      if (get().journey?.steps?.length) {
        return get().journey.steps.find(
          (step) =>
            step.id === currentStepId &&
            step.type === 'standard' &&
            step.completedTime === null
        );
      }
      return null;
    },

    setStatus: (status) => {
      // TODO
      let canHaveLiveVideo = false;
      //TODO: We may need to add Scheduled here, we need to check to see when we are going to be flipping to In Progress...
      if (['In Progress'].includes(status)) {
        canHaveLiveVideo = true;
      }

      set({ status, canHaveLiveVideo });
    },

    loadAnnotations: async (types) => {
      // First load all the actual annotation types so we have those...
      const annotationTypes = await get()
        .storeState('appStore')
        .ApiClient.getAnnotationTypes({
          installSid: get().installSid,
        });

      if (annotationTypes.successful) {
        // convert to an object for storage so it is easier to deal with...
        if (
          annotationTypes.data?.results &&
          annotationTypes.data.results.length
        ) {
          const localAnnotationTypes = annotationTypes.data.results.reduce(
            (acc, item) => {
              acc[item.id] = item;

              return acc;
            },
            {}
          );
          set({ annotationTypes: localAnnotationTypes });
        }
      } else {
        //TODO: IF this could not be loaded....we need to do something
      }

      // load all the actual annotations for this session...
      const sessionAnnotations = await get()
        .storeState('appStore')
        .ApiClient.getAnnotations({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
        });

      if (sessionAnnotations.successful) {
        // convert to an object for storage so it is easier to deal with...
        if (
          sessionAnnotations.data?.results &&
          sessionAnnotations.data.results.length
        ) {
          const recordingStartDate = new Date(
            get().sessionData.recordingStartDate
          );

          const localSessionAnnotations = {};

          for (const annotation of sessionAnnotations.data.results) {
            get().addAnnotation(annotation, localSessionAnnotations);
          }

          set({ annotations: localSessionAnnotations });
        }
      } else {
        //TODO: IF this could not be loaded....we need to do something
      }
    },

    // Helper function to replace an annotation if it exists with the same SID
    replaceOrAddAnnotation: (annotationsList, newAnnotation) => {
      // If the annotation has no SID, just add it
      if (!newAnnotation.sid) {
        return [...annotationsList, newAnnotation];
      }

      // Check if an annotation with the same SID already exists
      const existingIndex = annotationsList.findIndex(
        (item) => item.sid === newAnnotation.sid
      );

      if (existingIndex >= 0) {
        // Replace the existing annotation
        const updatedList = [...annotationsList];
        updatedList[existingIndex] = newAnnotation;
        return updatedList;
      } else {
        // Add as a new annotation
        return [...annotationsList, newAnnotation];
      }
    },

    addAnnotation: (annotation, sessionAnnotations) => {
      const recordingStartDate = new Date(get().sessionData.recordingStartDate);

      // based on the type, get the type so we can add it to the proper category...
      const type = get().annotationTypes[annotation.typeId];

      // calculate the start...
      const endDate = new Date(annotation.createdDate);
      annotation.start = Math.floor((endDate - recordingStartDate) / 1000);
      // TODO: Need to figure this out...
      annotation.color = 'info';
      annotation.type = type;

      if (!sessionAnnotations) {
        const localAnnotations = { ...get().annotations };
        if (!localAnnotations[type.categoryId]) {
          localAnnotations[type.categoryId] = [];
        }

        // Use the helper function instead of push
        localAnnotations[type.categoryId] = get().replaceOrAddAnnotation(
          localAnnotations[type.categoryId],
          annotation
        );
        set({ annotations: localAnnotations });
      } else {
        if (!sessionAnnotations[type.categoryId]) {
          sessionAnnotations[type.categoryId] = [];
        }
        // Use the helper function for the session annotations too
        sessionAnnotations[type.categoryId] = get().replaceOrAddAnnotation(
          sessionAnnotations[type.categoryId],
          annotation
        );
      }
    },

    // Remove an annotation by SID
    getAnnotationFromStore: (annotationSid) => {
      const localAnnotations = { ...get().annotations };
      // loop all the categories to find the annotation object
      for (const categoryId in localAnnotations) {
        if (!localAnnotations[categoryId]) {
          continue;
        }
        const annotation = localAnnotations[categoryId].find(
          (item) => item.sid === annotationSid
        );
        if (annotation) {
          return annotation;
        }
      }
      return null;
    },

    deleteAnnotation: async (annotationSid) => {
      // get the annotation to ensure it is valid...
      const annotation = get().getAnnotationFromStore(annotationSid);
      if (!annotation) {
        // we are going to return true to avoid any potential race conditions with another proctoring removing it first...
        return { successful: true };
      }

      const deleteAnnotationResult = await get()
        .storeState('appStore')
        .ApiClient.deleteAnnotation({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
          annotationSid: annotationSid,
        });

      if (deleteAnnotationResult.successful) {
        // remove the annotation from the store
        get().removeAnnotation(annotationSid);
        return { successful: true };
      } else {
        return { successful: false };
      }
    },

    updateAnnotation: async (annotationSid, payload) => {
      // get the annotation to ensure it is valid...
      const annotation = get().getAnnotationFromStore(annotationSid);
      const localAnnotations = { ...get().annotations };
      if (!annotation) {
        // we are going to return true to avoid any potential race conditions with another proctoring removing it first...
        return { successful: true };
      }

      const updateAnnotationResult = await get()
        .storeState('appStore')
        .ApiClient.updateAnnotation({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
          annotationSid: annotationSid,
          payload,
        });

      if (updateAnnotationResult.successful) {
        // Update the annotation with the new payload
        const updatedAnnotation = {
          ...annotation,
          ...payload,
        };

        // Update the annotation in the local store
        localAnnotations[annotation.type.categoryId] =
          get().replaceOrAddAnnotation(
            localAnnotations[annotation.type.categoryId],
            updatedAnnotation
          );

        // Update the annotations in the store
        set({ annotations: localAnnotations });

        // If is the same annotation as the one being managed...update the managing one too...
        if (get().managingAnnotation?.sid === annotationSid) {
          set({ managingAnnotation: updatedAnnotation });
        }

        return { successful: true };
      } else {
        return { successful: false };
      }
    },

    // Remove an annotation by SID
    removeAnnotation: (annotationSid) => {
      const localAnnotations = { ...get().annotations };
      // loop all the categories to find the annotation object
      for (const categoryId in localAnnotations) {
        if (!localAnnotations[categoryId]) {
          continue;
        }
        const annotation = localAnnotations[categoryId].find(
          (item) => item.sid === annotationSid
        );
        if (annotation) {
          // remove the annotation
          localAnnotations[categoryId] = localAnnotations[categoryId].filter(
            (item) => item.sid !== annotationSid
          );
          set({ annotations: localAnnotations });
        }
      }
    },

    setManagingAnnotation: (annotation) => {
      set({ managingAnnotation: annotation });
    },

    /**
     * Gets all annotations from all categories combined into a single array
     * and sorted by createdDate with newest annotations first
     *
     * @param {Object} options - Filter options
     * @param {boolean} [options.onlyEnabled=true] - Only include annotations from enabled categories
     * @param {string|string[]} [options.types] - Filter by specific annotation type(s)/categoryId (e.g., 'flags', ['flags', 'notes'])
     * @returns {Array} Combined array of all annotations sorted by createdDate (newest first)
     * 
     * @example
     * // Get all enabled annotations
     * getAnnotations({ onlyEnabled: true })
     * 
     * @example
     * // Get only flags
     * getAnnotations({ types: 'flags' })
     * 
     * @example
     * // Get flags and notes
     * getAnnotations({ types: ['flags', 'notes'] })
     * 
     * @example
     * // Get all annotations regardless of enabled status
     * getAnnotations({ onlyEnabled: false })
     */
    getAnnotations: (options = {}) => {
      const { onlyEnabled = false, types = null } = options;

      const allAnnotations = [];
      let annotationsByType = { ...get().annotations };

      // Filter by specific type(s) if provided
      if (types) {
        const typeArray = Array.isArray(types) ? types : [types];
        const filteredTypes = {};
        typeArray.forEach(type => {
          if (annotationsByType[type]) {
            filteredTypes[type] = annotationsByType[type];
          }
        });
        annotationsByType = filteredTypes;
      }

      // Filter out any annotations that are not enabled
      if (onlyEnabled) {
        const enabledAnnotations = get().storeState('playerStore').displayMarkers;

        Object.keys(annotationsByType).forEach((categoryId) => {
          if (!enabledAnnotations.includes(categoryId)) {
            delete annotationsByType[categoryId];
          }
        });
      }

      // Loop through each annotation category
      Object.keys(annotationsByType).forEach((categoryId) => {
        // Skip if the category doesn't exist or is empty
        if (
          !annotationsByType[categoryId] ||
          !annotationsByType[categoryId].length
        ) {
          return;
        }

        // Add all annotations from this category to the combined array
        annotationsByType[categoryId].forEach((annotation) => {
          allAnnotations.push({
            ...annotation,
            categoryId, // Add the categoryId to each annotation for reference
          });
        });
      });

      // Sort by createdDate, newest first
      return allAnnotations.sort((a, b) => {
        const dateA = new Date(a.createdDate);
        const dateB = new Date(b.createdDate);
        return dateB - dateA; // Descending order (newest first)
      });
    },

    /**
     * Loads modality options for the current exam session.
     * If options are already cached in the store, returns them immediately.
     * Otherwise, fetches from ApiClient and caches the result.
     * @async
     * @function loadModalityOptions
     * @returns {{successful: boolean, data: Array}} response object with success flag and array of modality options
     */
    loadModalityOptions: async () => {
      // Only load the modality options if the object is not already populated...
      if (get().modalityOptions?.length) {
        return { successful: true, data: get().modalityOptions };
      }
      const modalityOptions = await get()
        .storeState('appStore')
        .ApiClient.getModalityOptions({
          proctorTypeSubCategorySid: get().sessionData.proctorTypeSid,
        });

      if (modalityOptions.successful) {
        set({ modalityOptionGroups: modalityOptions.data });
        return { successful: true, data: modalityOptions.data };
      } else {
        //TODO: IF this could not be loaded....we need to do something
      }
    },

    invokeAction: async (payload) => {
      try {
        const actionResult = await get()
          .storeState('appStore')
          .room.localParticipant.performRpc({
            destinationIdentity: get().sessionSid,
            method: 'addAction',
            payload: JSON.stringify(payload),
          });
        const responseObject = JSON.parse(actionResult);
        return {
          successful: responseObject.success,
          data: null,
          error: responseObject?.error ? responseObject.error : null,
        };
      } catch (e) {
        return { successful: false, data: null, error: e.message };
      }
    },

    clearAnnotationType: (type) => {
      //const updatedAnnotations = { ...get().annotations };
      //delete updatedAnnotations[type];
      //set({annotations: updatedAnnotations})
    },

    setIncidentReport: (incidentReport) => {
      set({ incidentReport });
    },

    getIncidentReport: async () => {
      const incidentReports = await get()
        .storeState('appStore')
        .ApiClient.listIncidentReports({
          sessionSid: get().sessionSid,
          examSid: get().examSid,
          installSid: get().installSid,
        });

      if (incidentReports.successful) {
        if (incidentReports?.data?.results?.length > 0) {
          get().setIncidentReport(incidentReports.data.results[0]);
        } else {
          get().setIncidentReport(null);
        }
      } else {
        //TODO: Handle the error
      }
    },

    getHlsAuthenticationToken: () => {
      return getMediaAuthToken(get().storeState('appStore').spToken);
    },

    setupSyncListeners: () => {
      get().sync.emitter.on(SESSION_EVENTS.SYNC_DOC_UPDATED, function (data) {
        //console.log('SYNC_DOC_UPDATED', data);
      });

      get().sync.emitter.on(
        SESSION_EVENTS.SYNC_DOC_UPDATED_WITH_PREVIOUS_DATA,
        function (data) {
          // START:update the current modality options if things were properly updated...
          const updatedCurrentModalityOptions =
            data.currentData.currentModalityOptions;
          const previousCurrentModalityOptions =
            data.previousData.currentModalityOptions;
          if (
            updatedCurrentModalityOptions !== previousCurrentModalityOptions
          ) {
            // update the current modality options
            set({ currentModalityOptions: updatedCurrentModalityOptions });
          }
          // END: update the current modality options

          //console.log('SYNC_DOC_UPDATED_WITH_PREVIOUS_DATA', data);
        }
      );

      get().sync.emitter.on(
        SESSION_EVENTS.EXTENSION_ACTIONS_ADDED,
        function (data) {
          //console.log('EXTENSION_ACTIONS_ADDED', data);
        }
      );

      get().sync.emitter.on(
        SESSION_EVENTS.SESSION_FORCE_CLOSED,
        function (data) {
          //console.log('SESSION_FORCE_CLOSED', data);
        }
      );

      get().sync.emitter.on(
        SESSION_EVENTS.SESSION_JOURNEY_STEP_ID_UPDATED,
        async function (data) {
          await get().refreshSessionJourney();
          //console.log('SESSION_JOURNEY_STEP_ID_UPDATED', data);
        }
      );

      get().sync.emitter.on(SESSION_EVENTS.STATUS_UPDATED, function (data) {
        // update the local status...
        get().setStatus(data.newValue);
      });
    },
  }));
